import { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [deactivationReason, setDeactivationReason] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const checkTokenAndRedirect = () => {
    const token = localStorage.getItem('superadminToken');
    if (!token) {
      navigate('/superadmin/login');
      return false;
    }
    return token;
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('superadminToken');
      if (!token) {
        navigate('/superadmin/login');
        return;
      }

      const response = await fetch('https://didrouteapi.com/api/superadmin/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('superadminToken');
          localStorage.removeItem('superadminUser');
          navigate('/superadmin/login');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setUsers(data.data);
        setError(null);
      } else {
        throw new Error(data.error || 'Failed to fetch users');
      }
    } catch (err) {
      if (err.message.includes('Invalid or expired token')) {
        localStorage.removeItem('superadminToken');
        localStorage.removeItem('superadminUser');
        navigate('/superadmin/login');
        return;
      }
      setError(err.message || 'Failed to fetch users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = (userId, currentStatus) => {
    if (currentStatus) {  // If currently active, show modal for deactivation
      setSelectedUserId(userId);
      setShowReasonModal(true);
    } else {  // If currently inactive, activate directly
      toggleUserStatus(userId);
    }
  };

  const toggleUserStatus = async (userId, reason = null) => {
    const token = checkTokenAndRedirect();
    if (!token) return;

    try {
      const response = await fetch(`https://didrouteapi.com/api/superadmin/users/${userId}/toggle-status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ description: reason })
      });

      if (response.status === 401) {
        localStorage.removeItem('superadminToken');
        localStorage.removeItem('superadminUser');
        navigate('/superadmin/login');
        return;
      }

      const data = await response.json();
      if (data.success) {
        fetchUsers(); // Refresh users list
        setShowReasonModal(false);
        setDeactivationReason('');
      } else {
        setError(data.error || 'Failed to update user status');
      }
    } catch (err) {
      console.error('Error toggling user status:', err);
      setError('Failed to update user status. Please try again.');
    }
  };

  const handleDeactivationSubmit = (e) => {
    e.preventDefault();
    if (!deactivationReason.trim()) {
      setError('Please provide a reason for deactivation');
      return;
    }
    toggleUserStatus(selectedUserId, deactivationReason);
  };

  const filteredUsers = users.filter((user) =>
    Object.values(user).some((value) =>
      value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <div className="text-red-700">Error: {error}</div>
        <button 
          onClick={fetchUsers}
          className="mt-2 text-sm text-red-600 hover:text-red-800"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white p-2 rounded-lg shadow-lg">
      <div className="mx-auto max-w-full">
        <div className="bg-white py-10">
          <div className="px-4 sm:px-4 lg:px-4">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  Users Management
                </h1>
              </div>

              <div className="mt-4 sm:mt-0 sm:ml-16">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full rounded-md border border-gray-700 placeholder:text-gray-900 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-4 py-2"
                />
              </div>
            </div>

            <div className="mt-8 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 custom-scrollbar">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-800">
                    <thead>
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-xs font-medium text-black sm:pl-0">
                          Name
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Email
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Phone
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Description
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Status
                        </th>
                        <th className="px-3 py-3.5 text-left text-xs font-medium text-black">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td
                            colSpan="5"
                            className="px-6 py-4 text-center text-gray-500"
                          >
                            No users found
                          </td>
                        </tr>
                      ) : (
                        filteredUsers.map((user) => (
                          <tr key={user.id}>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-gray-900 sm:pl-0">
                              {user.name}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                              {user.email}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                              {user.phone || "N/A"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                              {user.description || "N/A"}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-900">
                              <Switch
                                checked={user.is_verified}
                                onChange={() =>
                                  handleStatusToggle(user.id, user.is_verified)
                                }
                                className={`${
                                  user.is_verified
                                    ? "bg-green-600"
                                    : "bg-red-400"
                                } relative inline-flex h-4 w-8 items-center rounded-full`}
                              >
                                <span className="sr-only">
                                  Toggle user status
                                </span>
                                <span
                                  className={`${
                                    user.is_verified
                                      ? "translate-x-4"
                                      : "translate-x-1"
                                  } inline-block h-3 w-3 transform rounded-full bg-white transition`}
                                />
                              </Switch>
                            </td>
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                              <button
                                onClick={() =>
                                  handleStatusToggle(user.id, user.is_verified)
                                }
                                className="text-indigo-600 font-semibold hover:text-indigo-900"
                              >
                                {user.is_verified ? "Deactivate" : "Activate"}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Deactivation Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900">
                Deactivation Reason
              </h3>
              <form onSubmit={handleDeactivationSubmit} className="mt-4">
                <textarea
                  value={deactivationReason}
                  onChange={(e) => setDeactivationReason(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  rows="4"
                  placeholder="Please provide a reason for deactivation..."
                  required
                />
                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowReasonModal(false);
                      setDeactivationReason("");
                    }}
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Deactivate
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 